import { createPortal } from "react-dom";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FullscreenModal, Button, CalendarInput, DropFileInput } from "shared";
import Input from "shared/Input/Input";
import {
  useCloseAsset,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import "./CloseAssetModal.scss";

interface CloseAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
  slug?: string;
}

interface FormValues {
  salePrice: string;
  closedAt: string;
  contractFile: File | null;
  weight: string;
}

const getValidationSchema = (slug?: string) =>
  Yup.object({
    salePrice: Yup.number()
      .required("Cena sprzedaży jest wymagana")
      .positive("Cena musi być dodatnia"),
    closedAt:
      slug === "precious_stones"
        ? Yup.string().notRequired()
        : Yup.string().required("Data sprzedaży jest wymagana"),
    contractFile: Yup.mixed().nullable(),
    weight: Yup.string().notRequired(),
  });

const CloseAssetModal = ({ isOpen, onClose, item, slug }: CloseAssetModalProps) => {
  const { mutateAsync: closeAsset, isPending } = useCloseAsset();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const initialValues: FormValues = {
    salePrice: "",
    closedAt: "",
    contractFile: null,
    weight: item?.details?.weight != null ? String(item.details.weight) : "",
  };

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    try {
      // Zamknij aktywo
      await closeAsset(
        {
          uuid: item.uuid,
          closedAt: values.closedAt,
          salePrice: Number(values.salePrice),
          currencyUuid: item.currency?.uuid,
        },
        {
          onSuccess: async (data) => {
            // Upload pliku umowy, jeśli został dodany
            if (values.contractFile instanceof File) {
              try {
                const userId: string | number =
                  (me as any)?.user?.id ?? (me as any)?.id ?? "";

                const uploaded = await uploadFile({
                  file: values.contractFile,
                  type: "attachment",
                  parentId: userId,
                });

                const fileUuid = Array.isArray(uploaded)
                  ? uploaded[0]?.uuid
                  : (uploaded as any)?.uuid;

                if (fileUuid && item.uuid) {
                  await attachFile({
                    investmentUuid: item.uuid,
                    fileUuid,
                    role: "closure_contract",
                  });
                }
              } catch {
                // Plik nie blokuje zamknięcia aktywa
              }
            }

            onClose();
          },
        },
      );
    } catch (error) {
      // Błąd jest obsługiwany przez hook
    }
  };

  if (!item) return null;

  const isPreciousStones = slug === "precious_stones";

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="close-asset-modal">
        <h2 className="fullscreen-modal__header">
          {isPreciousStones
            ? `Sprzedaż kamienia szlachetnego "${item.name || ""}"`
            : `Sprzedaż dzieła sztuki "${item.name || ""}"`}
        </h2>
        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema(slug)}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="close-asset-modal__form">
              <div className="close-asset-modal__fields">
                {isPreciousStones && (
                  <div
                    className="close-asset-modal__field"
                    style={{ opacity: 0.5 }}
                  >
                    <Input
                      type="number"
                      name="weight"
                      label="Masa sprzedanych Kamieni szlachetnych"
                      precision={0}
                      data-suffix="g"
                      disabled
                    />
                  </div>
                )}
                <div className="close-asset-modal__field">
                  <Input
                    type="number"
                    name="salePrice"
                    label="Cena sprzedaży"
                    precision={2}
                    data-suffix={item.currency?.currency}
                  />
                </div>
                {!isPreciousStones && (
                  <div className="close-asset-modal__field">
                    <Field
                      type="date"
                      name="closedAt"
                      label="Data sprzedaży"
                      component={CalendarInput}
                      errors={formik.errors}
                    />
                  </div>
                )}
                <div className="close-asset-modal__field">
                  <Field
                    label="Umowa"
                    name="contractFile"
                    id="contractFile"
                    as={DropFileInput}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    optional
                  />
                </div>
              </div>
              <div className="close-asset-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default CloseAssetModal;
